import { z } from 'zod';
import { PositiveIntString } from './primitives.ts';

export const MarketId = z.enum(['MAIN', 'EXACT_SIDE']);
export type MarketId = z.infer<typeof MarketId>;

/**
 * Traffic Pulse selections for thresholds L/H (C = final count):
 *  UNDER C<L · RANGE L<C<H · OVER C>H · EXACT C=L or C=H  (MAIN: disjoint & exhaustive)
 *  EXACT_LOW C=L · EXACT_HIGH C=H                          (EXACT_SIDE: may win together with EXACT)
 */
export const TrafficPulseSelection = z.enum(['UNDER', 'RANGE', 'OVER', 'EXACT', 'EXACT_LOW', 'EXACT_HIGH']);
export type TrafficPulseSelection = z.infer<typeof TrafficPulseSelection>;

/**
 * Every selection of every game. Which ones a round offers is fixed by its manifest (odds lines).
 *  Lane Duel: LANE_A (more A_TO_B crossings), LANE_B (more B_TO_A), TIE (equal, including 0–0)
 *  Next Class: CAR, HEAVY (bus/truck), MOTORCYCLE, NO_EVENT (first counted crossing in the window)
 *  Harbor Count: ZERO, ONE, TWO_PLUS (boat crossings)
 */
export const GameSelection = z.enum([
  'UNDER',
  'RANGE',
  'OVER',
  'EXACT',
  'EXACT_LOW',
  'EXACT_HIGH',
  'LANE_A',
  'LANE_B',
  'TIE',
  'CAR',
  'HEAVY',
  'MOTORCYCLE',
  'NO_EVENT',
  'ZERO',
  'ONE',
  'TWO_PLUS',
]);
export type GameSelection = z.infer<typeof GameSelection>;

export const SELECTION_MARKET: Record<GameSelection, MarketId> = {
  UNDER: 'MAIN',
  RANGE: 'MAIN',
  OVER: 'MAIN',
  EXACT: 'MAIN',
  EXACT_LOW: 'EXACT_SIDE',
  EXACT_HIGH: 'EXACT_SIDE',
  LANE_A: 'MAIN',
  LANE_B: 'MAIN',
  TIE: 'MAIN',
  CAR: 'MAIN',
  HEAVY: 'MAIN',
  MOTORCYCLE: 'MAIN',
  NO_EVENT: 'MAIN',
  ZERO: 'MAIN',
  ONE: 'MAIN',
  TWO_PLUS: 'MAIN',
};

export const CountThresholds = z
  .object({ low: z.number().int().min(1), high: z.number().int() })
  .refine((t) => t.high >= t.low + 2, { message: 'high must be >= low + 2' });
export type CountThresholds = z.infer<typeof CountThresholds>;

/** Total-return multiplier as an exact rational: payout = stake * numerator / denominator. */
export const Odds = z.object({
  numerator: PositiveIntString,
  denominator: PositiveIntString,
});
export type Odds = z.infer<typeof Odds>;

export const OddsLine = z.object({
  selection: GameSelection,
  odds: Odds,
  /** true when the price is an illustrative example, not a calibrated model price. */
  exampleOnly: z.boolean(),
});
export type OddsLine = z.infer<typeof OddsLine>;
